import { zodResolver } from '@hookform/resolvers/zod'
import {
  MediaObjectUrl,
  PointsExceptionError,
  Recognition,
  TenantMemberOrTeamDisplayData,
} from '@kudos/http-client'
import { createQualitiesSchema } from '@shared/forms/QualitiesAutoCompleteField'
import { createRichTextAreaSchema } from '@shared/forms/richTextEditorField/createRichTextAreaSchema'
import { Attachment } from '@shared/hooks/useFileMediaUpload'
import { TFunction } from '@shared/types/types'
import stripHtml from '@shared/utils/stripHtml'
import { useTranslatedField } from '@shared/utils/useTranslatedField'
import { MAX_ATTACHMENTS } from '@shared/wallPost/constants'
import useRecognitionModal from '@tenant/recognition/RecognitionModal/useRecognitionModal'
import { useConfigureRecognitionTabs } from '@tenant/recognition/useConfigureRecognitionTabs'
import useInProgressRecognitions from '@tenant/recognition/useInProgressRecognitions'
import { useT } from '@transifex/react'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import useSendRecognitionMutations from './useSendRecognitionMutations'

export const ISO_TIMESTAMP_REGEX = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/
export const MAX_POINTS_PER_RECIPIENT = 1000

const createRecognitionFormSchema = (t: TFunction, qualitiesRequired: boolean) =>
  z
    .object({
      sendTo: z
        .array(z.custom<TenantMemberOrTeamDisplayData>())
        .min(1, { message: t('Select a recipient') }),
      message: createRichTextAreaSchema(t),
      attachments: z
        .array(z.custom<Attachment & { attachmentSrc?: MediaObjectUrl; fileName?: string }>())
        .max(MAX_ATTACHMENTS),
      amount: z
        .number({ message: 'Points amount required' })
        .int()
        .min(0)
        .max(MAX_POINTS_PER_RECIPIENT, {
          message: t('Enter a point amount that is {maxPerRecipient} or less', {
            maxPerRecipient: MAX_POINTS_PER_RECIPIENT,
          }),
        }),
      gif: z.string().url().optional(),
      scheduledAt: z
        .string()
        .nullable()
        .refine(value => value === null || ISO_TIMESTAMP_REGEX.test(value), {
          message: t('Invalid date string'),
        })
        .refine(value => value === null || new Date(value) > new Date(), {
          message: t('Start date must be in the future'),
        }),
      tenantWide: z.boolean(),
      refinementId: z.string().optional(),
    })
    .and(createQualitiesSchema(t, qualitiesRequired))

export type RecognitionFormData = z.infer<ReturnType<typeof createRecognitionFormSchema>>

const useSendRecognitionForm = (
  setShowAnimation: (showAnimation: boolean) => void,
  qualitiesRequired: boolean,
  recognition?: Recognition,
  onClose?: () => void,
) => {
  const isEditMode = !!recognition
  const t = useT()
  const { closeDialog } = useRecognitionModal()
  const close = onClose ?? closeDialog
  const getTranslatedField = useTranslatedField()

  /** here lies all state management */
  const [pointsExceptions, setPointsExceptions] = useState<PointsExceptionError>()
  const [isFormClear, setIsFormClear] = useState<boolean>(false)
  const translatedQualities = recognition?.qualities?.map(quality => ({
    ...quality,
    name: getTranslatedField(quality, 'name'),
  }))

  /** actual form stuff! */
  const defaults = {
    sendTo:
      recognition?.sendTo?.map(
        recognitionSendTo =>
          ({
            ...recognitionSendTo.displayData,
          }) as TenantMemberOrTeamDisplayData,
      ) || [],
    message: recognition?.message || '',
    qualities: translatedQualities || [],
    attachments:
      recognition?.attachments.map((attachment, index) => ({
        mediaObjectId: attachment,
        attachmentSrc: recognition.attachmentUrls ? recognition?.attachmentUrls[index] : undefined,
        fileName: recognition.attachmentUrls
          ? recognition?.attachmentUrls[index].originalFilename
          : '',
      })) || [],
    amount: recognition?.amount || 0,
    gif: recognition?.gifs[0] || undefined,
    scheduledAt: recognition?.scheduledAt || null,
    tenantWide: recognition?.tenantWide ?? true,
  }

  const clearForm = () => {
    reset({
      sendTo: [],
      message: '',
      qualities: [],
      attachments: [],
      amount: 0,
      gif: undefined,
      scheduledAt: null,
      refinementId: undefined,
      tenantWide: true,
    })
    setIsFormClear(true)
  }

  const RecognitionFormSchema = createRecognitionFormSchema(t, qualitiesRequired)

  const form = useForm<RecognitionFormData>({
    resolver: zodResolver(RecognitionFormSchema),
    defaultValues: defaults,
  })
  const { formState, setError, control, reset, getValues, trigger } = form
  const { isSubmitSuccessful } = formState

  /** watch for form */
  const sendTo = useWatch({ control, name: 'sendTo' })
  const message = useWatch({ control, name: 'message' })

  const isFormDirty = formState.isDirty && (sendTo.length > 0 || stripHtml(message).length > 0)

  useEffect(() => {
    if (isSubmitSuccessful && !isEditMode && !formState.isDirty) {
      setPointsExceptions(undefined)
      clearForm()
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful, isEditMode, formState.isDirty])

  useEffect(() => {
    if (isFormClear && !isFormDirty) {
      close()
      setIsFormClear(false)
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormClear, isFormDirty])

  useEffect(() => {
    reset(defaults)
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recognition])

  const { publishRecognition, saveDraftRecognition, isPendingCreateOrSave } =
    useSendRecognitionMutations(setShowAnimation, setPointsExceptions, setError, recognition?.id)

  const { refetchDraftRecognitions } = useInProgressRecognitions()
  const { refetchDraftRecogitionCount } = useConfigureRecognitionTabs()

  /**
   * This function handles saveDraftRecognotion() without navBlocker().
   * Pass recognition?.id so the mutation uses the current id at call time (avoids stale closure in mobile drawer).
   */
  const handleSaveAsDraft = async () => {
    const values = getValues()
    await saveDraftRecognition(values, recognition?.id)
    reset(values) // resets form state with new values
  }

  const handleScheduleRecognition = async () => {
    const values = getValues()
    const isValid = await trigger()
    if (isValid) {
      // Note that schedule recognitions ARE drafts
      // that are both valid and have a scheduledAt.
      await saveDraftRecognition(values, recognition?.id)
      reset(values) // resets form state with new values
    }
    return isValid
  }

  const handleDiscardChanges = () => {
    reset(defaults)
  }

  /**
   * This function handles the async saveDraftRecognition() when
   * navBlocker() is activated.
   */
  const handleKeepChanges = async () => {
    const values = getValues()
    await saveDraftRecognition(values, recognition?.id)
    reset(values)
    /**
     * navBlocker() also blocks the onSuccess callback of saveDraftRecognition().
     * Refetching data is required to ensure consistency if/when the user
     * decides to keep the changes.
     */
    refetchDraftRecognitions()
    refetchDraftRecogitionCount()
  }

  const handleResetRecognitionForm = () => {
    clearForm()
  }

  return {
    form,
    isEditMode,
    isFormDirty,
    pointsExceptions,
    setPointsExceptions,
    publishRecognition,
    handleSaveAsDraft,
    handleScheduleRecognition,
    handleDiscardChanges,
    handleKeepChanges,
    handleResetRecognitionForm,
    isPendingCreateOrSave,
  }
}

export default useSendRecognitionForm
